import FVector from '@data/geometry/FVector';
import { BinaryReader } from '../serialization/common/BinaryReader';
import Serializable, { HeaderSchema } from './Serializable';
import Grid from 'Geodata/grid';
type BaseSchema = {
    version: number;
    simKind: number;
};
type SchemaV5 = BaseSchema & {
    frameMax: number;

    frameNames: string[];
    scalarResults: number[][];
    vectorResults: FVector[][];
};

type SchemaV14 = BaseSchema & {
    sensorNumber: number;

    resultId: number;
    resultLabel: string;
    unitOfMeasure: string;
    frameMax: number;

    frameNames: string[];
    scalarResults: number[][];
    vectorResults: FVector[][];
};

type PreviousSchema = SchemaV14 | SchemaV5;

export type LatestResultSchema = SchemaV14;

class ResultSerializer extends Serializable<PreviousSchema, LatestResultSchema> {
    protected migrateToLatest(anyPrevious: PreviousSchema): LatestResultSchema {
        let migrated = anyPrevious;
        if (anyPrevious.version <= 5)
            migrated = {
                ...anyPrevious,
                sensorNumber: Grid.sensorPointsN,
                resultId: 0,
                resultLabel: 'fake-label',
                unitOfMeasure: 'no unit of measure',
            };

        return migrated as LatestResultSchema;
    }

    protected deserializeContent(header: HeaderSchema, reader: BinaryReader): LatestResultSchema {
        switch (header.version) {
            case 14:
                return this.deserializeV14(header, reader);
            case 5:
                return this.deserializeV5(header, reader);
            default:
                throw new Error(`Unsupported version: ${header.version}. Update your files`);
        }
    }

    private deserializeV14(header: HeaderSchema, reader: BinaryReader): LatestResultSchema {
        const sensorNumber = reader.readUInt32();
        const resultId = reader.readUInt8();

        const resultLabel = reader.readString();
        const unitOfMeasure = reader.readString();

        const frameMax = reader.readUInt8();

        const scalarResults: number[][] = [];
        const vectorResults: FVector[][] = [];
        const frameNames: string[] = [];

        for (let f = 0; f < frameMax; f++) {
            const frameName = reader.readString();
            frameNames.push(frameName);

            const scalarValues: number[] = [];
            const vectorialValues: FVector[] = [];
            for (let N = 0; N < sensorNumber; N++) {
                let scalarValue = reader.readFloat32();
                const vectorialValue = reader.readVec3();

                scalarValue = Number.isFinite(scalarValue) ? scalarValue : 0;

                scalarValues[N] = scalarValue;
                vectorialValues[N] = vectorialValue;
            }
            scalarResults[f] = scalarValues;
            vectorResults[f] = vectorialValues;
        }
        return this.migrateToLatest({
            version: header.version,
            simKind: header.simKind,
            sensorNumber,
            resultId,
            resultLabel,
            unitOfMeasure,
            frameMax,
            frameNames,
            scalarResults,
            vectorResults,
        });
    }
    private deserializeV5(header: HeaderSchema, reader: BinaryReader): LatestResultSchema {
        const frameMax = reader.readUInt8();

        const scalarResults: number[][] = [];
        const vectorResults: FVector[][] = [];
        const frameNames: string[] = [];

        for (let f = 0; f < frameMax; f++) {
            const frameName = reader.readString();
            frameNames.push(frameName);

            const scalarValues: number[] = [];
            const vectorialValues: FVector[] = [];
            for (let N = 0; N < Grid.sensorPointsN; N++) {
                let scalarValue = reader.readFloat32();
                const vectorialValue = reader.readVec3();

                scalarValue = Number.isFinite(scalarValue) ? scalarValue : 0;

                scalarValues[N] = scalarValue;
                vectorialValues[N] = vectorialValue;
            }
            scalarResults[f] = scalarValues;
            vectorResults[f] = vectorialValues;
        }
        return this.migrateToLatest({
            version: header.version,
            simKind: header.simKind,
            frameMax,
            frameNames,
            scalarResults,
            vectorResults,
        });
    }
}

export default ResultSerializer;
